import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "reactstrap";
import { CheckCircle, Upload, AlertTriangle } from "react-feather";
import { getNearpayAccount, saveNearpayAccount } from "../../utils/apiCalls";
import { C } from "../reporting/reportingStyles";
import { RCard } from "../reporting/ReportingShared";

/**
 * The merchant's NearPay ACCOUNT credentials — the key every terminal token is signed from.
 *
 * It sits on the Payment Terminals page rather than in a general settings screen because the
 * dependency is the point: without a key uploaded here, the Generate Token button on every row
 * below does nothing. Putting them on separate pages would let an admin stare at a failing button
 * with no idea what it wants.
 *
 * WHAT THIS DELIBERATELY CANNOT DO
 * It never displays a stored secret, only whether one is present. NearPay lets you download
 * `pos_key.pem` exactly once, so there is no scenario where reading ours back out of the database
 * is the right answer — if an admin has lost track of which key is loaded, the fix is a new key
 * from NearPay, not a reveal button here.
 */
// The merchant comes from the app header's selector, not from a picker of its own. A page-local
// dropdown can disagree with the header, and an admin who uploads a key while the header names a
// different merchant has no way to see which one it landed on.
const NearpayAccountCard = ({ isAdmin, onChanged }) => {
  const ownMerchantId = localStorage.getItem("MerchantId") || "";
  const { selectedMerchant } = useSelector((state) => state.merchant || {});

  // A merchant user has no header selector — they are pinned to their own account, and the API
  // scopes them to it regardless of what any id says.
  const merchantId = isAdmin ? selectedMerchant || "" : ownMerchantId;

  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [keyFile, setKeyFile] = useState(null);
  const [accountUuid, setAccountUuid] = useState("");
  const [accountUuidType, setAccountUuidType] = useState("client");
  const [apiKey, setApiKey] = useState("");

  const load = useCallback(async (id) => {
    if (!id) {
      setStatus(null);
      return;
    }
    setIsLoading(true);
    const data = await getNearpayAccount(id);
    setStatus(data);
    // Prefill what is safe to show, so saving one field does not blank the others in the form.
    setAccountUuid(data?.accountUuid || "");
    setAccountUuidType(data?.accountUuidType || "client");
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load(merchantId);
  }, [merchantId, load]);

  const dirty = useMemo(
    () =>
      !!keyFile ||
      !!apiKey ||
      (accountUuid || "") !== (status?.accountUuid || "") ||
      (accountUuidType || "") !== (status?.accountUuidType || "client"),
    [keyFile, apiKey, accountUuid, accountUuidType, status],
  );

  const handleSave = async () => {
    if (!merchantId) return;

    const body = new FormData();
    if (keyFile) body.append("key", keyFile);
    // Sent only when changed. The API treats an absent field as "leave alone", which is what keeps
    // an api-key edit from wiping the private key.
    if ((accountUuid || "") !== (status?.accountUuid || "")) body.append("accountUuid", accountUuid);
    if ((accountUuidType || "") !== (status?.accountUuidType || "")) {
      body.append("accountUuidType", accountUuidType);
    }
    if (apiKey) body.append("apiKey", apiKey);

    setIsSaving(true);
    const result = await saveNearpayAccount(merchantId, body);
    setIsSaving(false);

    if (result) {
      setStatus(result);
      setKeyFile(null);
      setApiKey("");
      // The Generate Token buttons below become usable the moment a key lands, so the parent
      // refreshes rather than leaving them looking broken until a manual reload.
      if (onChanged) onChanged();
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    background: C.white,
  };

  const StatusChip = ({ ok, label, missingLabel }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: ok ? C.greenLight : C.orangeLight,
        color: ok ? C.green : C.orange,
      }}
    >
      {ok ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
      {ok ? label : missingLabel}
    </span>
  );

  return (
    <RCard>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <h5 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>NearPay Account</h5>
        {status && (
          <>
            <StatusChip ok={status.hasPrivateKey} label="Signing key loaded" missingLabel="No signing key" />
            <StatusChip ok={status.hasApiKey} label="API key set" missingLabel="No API key" />
          </>
        )}
      </div>
      {!merchantId ? (
        <div style={{ fontSize: 13, color: C.textLight }}>
          Pick a merchant in the header to see or set its NearPay credentials.
        </div>
      ) : isLoading || !status ? (
        <div style={{ fontSize: 13, color: C.textLight }}>Loading…</div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            <div>
              <label style={labelStyle}>Private key file</label>
              <label
                style={{
                  ...inputStyle,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  color: keyFile ? C.text : C.textLight,
                }}
              >
                <Upload size={14} />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {keyFile ? keyFile.name : status.hasPrivateKey ? "Replace stored key…" : "Choose the key file…"}
                </span>
                {/*
                  No `accept` filter. NearPay's dashboard names the download arbitrarily — the real
                  file arrives as `1.64 Kb`, with no extension — and `accept` greys out anything that
                  does not match, so the filter would hide the very file being asked for. The server
                  parses the key itself and rejects anything that is not one.
                */}
                <input
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => setKeyFile(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            <div>
              <label style={labelStyle}>Account UUID</label>
              <input
                style={inputStyle}
                value={accountUuid}
                onChange={(e) => setAccountUuid(e.target.value)}
                placeholder="From the dashboard"
              />
            </div>

            <div>
              <label style={labelStyle}>Dashboard type</label>
              <select
                style={inputStyle}
                value={accountUuidType}
                onChange={(e) => setAccountUuidType(e.target.value)}
              >
                <option value="client">Client dashboard</option>
                <option value="merchant">Merchant dashboard</option>
              </select>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.5 }}>
                Decides whether the token carries <code>client_uuid</code> or{" "}
                <code>merchant_uuid</code>. The wrong one fails on the device.
              </div>
            </div>

            <div>
              <label style={labelStyle}>POS API key</label>
              <input
                style={inputStyle}
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={status.hasApiKey ? "Stored — type to replace" : "NearPay API key"}
                autoComplete="new-password"
              />
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.5 }}>
                Not needed to take payments, but required to verify screen payments and recover
                charged-but-lost orders. Without it you'll get "payment could not be verified" alerts.
              </div>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <Button
              onClick={handleSave}
              disabled={!dirty || isSaving}
              style={{
                background: C.coral,
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontSize: 13,
                fontWeight: 600,
                opacity: !dirty || isSaving ? 0.5 : 1,
              }}
            >
              {isSaving ? "Saving…" : "Save Credentials"}
            </Button>
          </div>
        </>
      )}
    </RCard>
  );
};

export default NearpayAccountCard;
